export function pluralizeUnit(value: number, unit: 'days' | 'months' | 'years'): string {
  switch (unit) {
    case 'months':
      return value === 1 ? 'měsíc' : value >= 2 && value <= 4 ? 'měsíce' : 'měsíců';
    case 'years':
      return value === 1 ? 'rok' : value >= 2 && value <= 4 ? 'roky' : 'let';
    default:
      return value === 1 ? 'den' : value >= 2 && value <= 4 ? 'dny' : 'dnů';
  }
}

/**
 * Extrahuje hodnotu z autocomplete vstupu podle zadaného indexu.
 *
 * Pokud řetězec obsahuje oddělovač, rozdělí jej a vrátí hodnotu na zadaném indexu.
 * Pokud oddělovač není nalezen, vrátí celý vstup.
 *
 * @param query - Vstupní řetězec, který může obsahovat více hodnot oddělených oddělovačem.
 * @param index - Index hodnoty, kterou chceme získat, pokud je možné řetězec rozdělit.
 * @param delimiter - Oddělovač používaný pro rozdělení řetězce. Výchozí hodnota je " - ".
 * @returns Vrací hodnotu odpovídající zadanému indexu, nebo celý vstup, pokud oddělovač chybí.
 */
export function extractValueFromAutocompleteInput(query: string, index = 0, delimiter = ' - '): string {
  if (query.includes(delimiter)) {
    const parts = query.split(delimiter);
    if (index >= 0 && index < parts.length) {
      return parts[index].trim();
    }
  }
  return query.trim();
}

const ELLIPSIS = '…';

/** Jeden zobrazený znak včetně emoji sekvencí a diakritiky ze spojovacích znaků (převzato z truncate-html / lodash). */
const GRAPHEME =
  /\ud83c[\udffb-\udfff](?=\ud83c[\udffb-\udfff])|(?:[^\ud800-\udfff][\u0300-\u036f\ufe20-\ufe23\u20d0-\u20f0]?|[\u0300-\u036f\ufe20-\ufe23\u20d0-\u20f0]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\ud800-\udfff])[\ufe0e\ufe0f]?(?:[\u0300-\u036f\ufe20-\ufe23\u20d0-\u20f0]|\ud83c[\udffb-\udfff])?(?:\u200d(?:[^\ud800-\udfff]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff])[\ufe0e\ufe0f]?(?:[\u0300-\u036f\ufe20-\ufe23\u20d0-\u20f0]|\ud83c[\udffb-\udfff])?)*/g;

/**
 * Zkrátí HTML na `length` viditelných znaků a zachová značky. Za oříznutý text připojí výpustku, další uzly odebere.
 * Chování odpovídá knihovně truncate-html (výchozí volby), která do prohlížeče tahala cheerio (~400 kB). HTML se
 * parsuje v inertním `<template>` (skripty se nespustí, obrázky nenačtou).
 */
export function truncateHtml(html: string, length: number): string {
  if (!html || !(length >= 1) || length === Infinity) return html;

  const template = document.createElement('template');
  template.innerHTML = html;
  let remaining = length;

  const truncateText = (text: string, isLastNode: boolean): string => {
    // Po sloučení bílých znaků zbývá jako mezera jen ' '.
    const collapsed = text.replace(/\s+/g, ' ');
    const chars = collapsed.match(GRAPHEME) ?? [];

    let idx = 0;
    let count = 0;
    let prevIsBlank = false;
    while (idx < chars.length) {
      const curIsBlank = chars[idx++] === ' ';
      if (count === remaining) {
        // Mezery za limitem se ještě vejdou, první další znak už ne.
        if (prevIsBlank && curIsBlank) continue;
        idx--;
        break;
      }
      if (!(curIsBlank && prevIsBlank)) count++;
      prevIsBlank = curIsBlank;
    }

    remaining -= count;
    if (remaining) return collapsed;

    const cut = chars.slice(0, idx).join('');
    return cut + (cut === collapsed && isLastNode ? '' : ELLIPSIS);
  };

  const travelChildren = (parent: Node, isParentLastNode: boolean): void => {
    const children = Array.from(parent.childNodes);
    children.forEach((node, idx) => {
      const isLastNode = isParentLastNode && idx === children.length - 1;
      if (remaining && node instanceof Text) {
        node.data = truncateText(node.data, isLastNode);
      } else if (remaining && node instanceof Element) {
        travelChildren(node, isLastNode);
      } else {
        node.parentNode?.removeChild(node);
      }
    });
  };

  travelChildren(template.content, true);
  return template.innerHTML;
}
